import classNames from 'classnames';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Header from '@/components/Header/Header';
import { showToast } from '@/components/Toast/Toast';
import { ToastTypes } from '@/components/Toast/Toast.types';
import { useSTKContext } from '@/context/context';
import { useActiveGlobalFreezes } from '@/services/freeze/freeze.service';
import { useGetAllStores } from '@/services/stores/stores.service';
import useFranchiseeAlias from '@/utils/useFranchiseeAlias/useFranchiseeAlias';
import styles from './NewMenus.module.css';
import Tabs from '@/components/Tabs/Tabs';
import StoresTabItem from './StoresTabItem/StoresTabItem';
import MenuHeader from './MenuHeader/MenuHeader';
import StoreGroupsTabItem from './StoreGroupsTabItem/StoreGroupsTabItem';
import { StoreAndGroupType } from '@/context/context.types';
import mockData from './Internal/mockData/stores2.json';
import _ from 'lodash/';

const Menus: FC = () => {
  const { t } = useTranslation();
  const { setStoresAndGroups } = useSTKContext();
  const { alias } = useFranchiseeAlias();
  const { data: activeGlobalFreezeData } = useActiveGlobalFreezes(true);
  const freezeExists = activeGlobalFreezeData?.data && activeGlobalFreezeData?.data?.length > 0;
  const [selectedRows, setSelectedRows] = useState<{ [number: number]: boolean }>({});
  const { data: stores = [], isLoading, isError } = useGetAllStores(true);
  const [activeTab, setActiveTab] = useState(0);
  const [groupAndStores, setGroupAndStores] = useState<Map<string, string[]>>(new Map<string, string[]>());
  const [selection, setSelection] = useState<{ stores: string[]; groups: string[] }>({ stores: [], groups: [] });

  const storesGroup = useMemo(() => _.groupBy(stores, 'groupName'), [stores]);
  const sortedStores = stores.sort((a, b) => (a.groupName ?? '').localeCompare(b.groupName ?? ''));

  const handleTabClick = (indexOfClickedTab: number) => {
    setActiveTab(indexOfClickedTab);
  };

  if (isError) {
    showToast({
      toastType: ToastTypes.Error,
      label: t('error.fetchingStoreData'),
      id: 'storeFetchError',
    });
  }

  const saveStores = (storeNos: string[]) => {
    setStoresAndGroups({
      origin: 'tab-store',
      stores: storeNos.map((store) => ({
        storeGroup: stores.find((storeDataBase) => storeDataBase.storeNo === store)?.groupName,
        storeNo: store,
      })),
      countByStoreGroup: Object.entries(storesGroup).map(([groupName, storesData]) => ({
        storeGroup: groupName,
        count: storesData.length,
      })),
    });
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 1:
        return (
          <StoresTabItem
            isLoading={isLoading}
            stores={sortedStores}
            selectedRows={selectedRows}
            setSelectedRows={setSelectedRows}
            onStoresSelectionChange={(storeNos: string[]) => saveStores(storeNos)}
          />
        );

      default:
        return (
          <StoreGroupsTabItem
            stores={stores}
            selectedStores={selection.stores}
            selectedGroups={Array.from(groupAndStores.keys())}
            onStoresSelectionChange={(storeAndGroup: StoreAndGroupType) => setStoresAndGroups(storeAndGroup)}
          />
        );
    }
  };

  return (
    <div>
      <div className={styles.mainDiv}>
        <Header title={t('menu.title')} breadcrumb={{ to: '/', label: t('menu.breadcrumb') }} includeReportsLink />
      </div>
      <div className={classNames(styles.page, { [styles.withAlias]: !alias, [styles.withFreeze]: freezeExists })}>
        <MenuHeader data={stores} selectedRows={selectedRows} navigateUrl="/menus/manage-menus/" />
        <div className={styles.tableContainer}>
          <Tabs
            activeTab={activeTab}
            onTabClick={handleTabClick}
            tabs={[
              { label: t('menu.tabs.storeGroups'), id: 'groups' },
              { label: t('menu.tabs.allStores'), id: 'stores' },
            ]}
          />
          <div className={styles.tabContainer}>{renderTabContent()}</div>
        </div>
      </div>
    </div>
  );
};

export default Menus;
